import { Injectable, Signal, computed, effect, signal } from '@angular/core';
import { Borrower } from '../../models/borrower';
import { Session } from '../../models/session';
import { CreditPlan } from '../../models/credit-plan';
import { Flags } from '../../models/flags';
import { Application } from '../../models/application';
import { SessionFlag } from '../../constants/flags';

/**
 * Represents an application state stored in memory
 */
export interface State {
    // Important values
    supplierId: string | null;
    sessionId: string | null;
    token: string | null;

    // Important models
    borrower: Borrower | null;
    session: Session | null;
    creditPlan: CreditPlan | null;
    application: Application | null;

    // Flags
    flags: Flags;
}

/**
 * StoreService handles storing and retrieving application state
 */
@Injectable({ providedIn: 'root' })
export class StoreService {
    /** State signal with initial application state */
    private stateSig = signal<State>({
        supplierId: null,
        sessionId: null,
        token: null,
        borrower: null,
        session: null,
        creditPlan: null,
        application: null,
        flags: {},
    });

    // Expose important state attributes
    readonly supplierId: Signal<string | null> = computed(() => this.stateSig().supplierId);
    readonly sessionId: Signal<string | null> = computed(() => this.stateSig().sessionId);
    readonly token: Signal<string | null> = computed(() => this.stateSig().token);
    readonly borrower: Signal<Borrower | null> = computed(() => this.stateSig().borrower);
    readonly session: Signal<Session | null> = computed(() => this.stateSig().session);
    readonly creditPlan: Signal<CreditPlan | null> = computed(() => this.stateSig().creditPlan);
    readonly application: Signal<Application | null> = computed(() => this.stateSig().application);
    readonly flags: Signal<Flags | null> = computed(() => this.stateSig().flags);

    // Constructor to debug state changes
    constructor() {
        effect(() => {
            const newState = this.stateSig();
            //console.log("[STORE]: New state reached", newState);
        })
    }

    /** Snapshot of the current state */
    get snapshot(): State {
        return this.stateSig();
    }

    /**
     * Set the session ID
     * @param sessionId The session ID to set
     */
    setSessionId(sessionId: string | null) {
        if (!sessionId || sessionId === this.sessionId()) return;

        this.stateSig.update((s) => ({ ...s, sessionId }));
    }

    /**
     * Set the authentication token
     * @param token The JWT auth token to set to
     */
    setToken(token: string | null) {
        if (!token || token === this.token()) return;

        this.stateSig.update((s) => ({ ...s, token }));
    }

    /**
     * Set the borrower object in the store
     * @param borrower Borrower object to set to
     */
    setBorrower(borrower: Borrower | null) {
        this.stateSig.update((s) => ({ ...s, borrower }));
    }

    /**
     * Set the session object in the store
     * @param session Session object to set to
     */
    setSession(session: Session | null) {
        this.stateSig.update((s) => ({ ...s, session }));
    }

    /**
     * Set the credit plan object in the store
     * @param creditPlan Credit plan object to set to
     */
    setCreditPlan(creditPlan: CreditPlan | null) {
        this.stateSig.update((s) => ({ ...s, creditPlan }));
    }

    /**
     * Set the application object in the store
     * @param application Application object to set to
     */
    setApplication(application: Application | null) {
        this.stateSig.update((s) => ({ ...s, application }));
    }

    /**
     * Set a provided flag in the state
     * @param flag Flag to set
     * @param value True or false
     */
    setFlag(flag: SessionFlag, value: boolean) {
        this.stateSig.update((s) => ({ 
            ...s, 
            flags: { 
                ...s.flags, 
                [flag]: value 
            } 
        }));
    }

    /**
     * Initialize the service by parsing the supplier ID from the URL path
     * This is called during application bootup
     */
    initialize(): Promise<void> {
        return new Promise((resolve) => {
            const path = window.location.pathname;
            const supplierId = this.parseCustomerIdFromPath(path);

            if (supplierId) {
                this.stateSig.update((s) => ({ ...s, supplierId }))
            } else {
                console.warn('[STORE-SERVICE]: No supplier ID found in URL path');
            }

            resolve();
        });
    }

    /**
     * Parse customer ID from the URL path
     * Expects format: /{customerId}/... or /{customerId}
     * 
     * @param path The URL path to parse
     * @returns The customer ID or null if not found
     */
    private parseCustomerIdFromPath(path: string): string | null {
        // Remove leading slash and split by '/'
        const segments = path.replace(/^\/+/, '').split('/');

        // The first segment should be the customer ID
        if (segments.length > 0 && segments[0]) {
            return segments[0];
        }

        return null;
    }
}